'use client';

import Link from 'next/link';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The owner's popup. KIT says what to show; whether this visitor already saw
 * it is ours to decide: `once` remembers in localStorage, `everyVisit` in
 * sessionStorage (so it returns on the next visit, not on every page).
 *
 * It waits until the visitor has scrolled a third of the page (or 15 seconds), never opens while the privacy banner is on screen,
 * closes on Escape and on the backdrop, and returns focus where it came from.
 */
export function SitePopup({
  headline,
  body,
  ctaText,
  ctaHref,
  frequency,
  imageUrl,
  imageAlt,
  version,
}: {
  headline: string;
  body: string;
  ctaText: string;
  ctaHref: string;
  frequency: 'once' | 'everyVisit';
  imageUrl: string;
  imageAlt: string;
  version: string;
}) {
  const [open, setOpen] = useState(false);
  const closeRef = useRef<HTMLButtonElement>(null);
  const returnTo = useRef<HTMLElement | null>(null);
  const key = `kit_popup_${version}`;

  useEffect(() => {
    let seen = false;
    try {
      seen = (frequency === 'once' ? localStorage : sessionStorage).getItem(key) === '1';
    } catch {
      /* storage blocked: show it, it can be closed */
    }
    if (seen) return;
    let t: ReturnType<typeof setTimeout>;
    // Never interrupt: not over the privacy banner, not while a menu is open, not while someone types.
    const busy = () => {
      const a = document.activeElement;
      return (
        !!document.querySelector('section[aria-label="Privacy choices"]') ||
        !!document.querySelector('header [id^="mega-"]:not([hidden])') ||
        (a instanceof HTMLElement && (!!a.closest('header') || /^(INPUT|TEXTAREA|SELECT)$/.test(a.tagName)))
      );
    };
    const attempt = () => {
      if (busy()) {
        t = setTimeout(attempt, 3000);
        return;
      }
      returnTo.current = document.activeElement as HTMLElement | null;
      setOpen(true);
    };
    // Not on arrival: only once the visitor has read a third of the page, or after 15 seconds.
    let started = false;
    const start = () => {
      if (started) return;
      started = true;
      window.removeEventListener('scroll', onScroll);
      attempt();
    };
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (max > 0 && window.scrollY / max > 0.33) start();
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    t = setTimeout(start, 15000);
    return () => {
      clearTimeout(t);
      window.removeEventListener('scroll', onScroll);
    };
  }, [frequency, key]);

  const close = useCallback(() => {
    setOpen(false);
    try {
      (frequency === 'once' ? localStorage : sessionStorage).setItem(key, '1');
    } catch {
      /* ignore */
    }
    returnTo.current?.focus?.();
  }, [frequency, key]);

  useEffect(() => {
    if (!open) return;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" onClick={close}>
      <div className="absolute inset-0 bg-black/60" aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="site-popup-heading"
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md overflow-hidden rounded-card border border-line-2 bg-ink-950 shadow-2xl"
      >
        {imageUrl && (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={imageUrl} alt={imageAlt} className="aspect-[16/9] w-full object-cover" />
        )}
        <div className="p-6">
          <h2 id="site-popup-heading" className="text-xl font-semibold tracking-tight">{headline}</h2>
          <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-mist-300">{body}</p>
          {ctaText && ctaHref && (
            <div className="mt-5">
              {ctaHref.startsWith('/') ? (
                <Link href={ctaHref} onClick={close} className="btn btn-primary px-5 py-3 text-sm">{ctaText}</Link>
              ) : (
                <a href={ctaHref} onClick={close} rel="noopener" className="btn btn-primary px-5 py-3 text-sm">{ctaText}</a>
              )}
            </div>
          )}
        </div>
        <button
          ref={closeRef}
          type="button"
          onClick={close}
          aria-label="Close"
          className="absolute right-2 top-2 inline-flex h-11 w-11 items-center justify-center rounded-full bg-ink-950/80 text-mist-100 hover:bg-ink-900"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" /></svg>
        </button>
      </div>
    </div>
  );
}
